import * as React from 'react';
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  ListSubheader,
  Divider,
  Typography,
} from '@mui/material';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import { LoadingButton } from '@mui/lab';
import { DepositPaymentStatus } from '@prisma/client';
import StatusChip from './StatusChip';
import Badge from '../global/Badge';
import { Appointment, FooterAction, FooterActionGroup } from '../../interfaces';
import {
  getAppointmentStatusKey,
  isAppointmentDepositPaid,
} from '../../helpers/appointments';
import { getWaiverRequestStatus } from '../../helpers/waivers';
import { GetWaiversData } from '../../types/waivers';
import { formatDollarAmount } from '../../utils/global/helper';

const PLAIN_LABEL_BY_STATUS: Partial<Record<Appointment['status'], string>> = {
  QUEUED: 'In queue',
  ARCHIVED: 'Archived',
};

const SECONDARY_BADGE_PALETTE = {
  due: {
    background: 'var(--sidebar-surface)',
    border: '1px dashed var(--status-unconfirmed-border)',
    color: 'var(--status-unconfirmed-text)',
  },
  notSent: {
    background: 'var(--status-unconfirmed-fill)',
    border: '1px dashed var(--status-unconfirmed-border)',
    color: 'var(--status-unconfirmed-text)',
  },
  complete: {
    background: 'var(--status-confirmed-fill)',
    border: '1px solid var(--status-confirmed-border)',
    color: 'var(--status-confirmed-text)',
  },
  canceled: {
    background: 'var(--sidebar-surface)',
    border: '1px solid var(--status-canceled-border)',
    color: 'var(--status-canceled-text)',
  },
  pendingEmployee: {
    background: 'var(--sidebar-surface)',
    border: '1px solid #EC942C',
    color: '#EC942C',
  },
} as const;

function SecondaryBadge({
  text,
  palette,
  onClick,
}: {
  text: string;
  palette: keyof typeof SECONDARY_BADGE_PALETTE;
  onClick?: () => void;
}) {
  const { background, border, color } = SECONDARY_BADGE_PALETTE[palette];
  return (
    <Box
      component={onClick ? 'button' : 'span'}
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        height: 24,
        padding: '0 11px',
        borderRadius: 50,
        background,
        border,
        font: 'inherit',
        fontSize: 'var(--sidebar-font-xs)',
        fontWeight: 700,
        color,
        whiteSpace: 'nowrap',
        ...(onClick && {
          cursor: 'pointer',
          '&:hover': { filter: 'brightness(0.96)' },
        }),
      }}>
      {text}
    </Box>
  );
}

type Props = {
  appointment: Appointment;
  primaryAction: FooterAction | null;
  primaryActionLoading?: boolean;
  kebabGroups: FooterActionGroup[];
  showEdit: boolean;
  onEdit?: () => void;
  onClose: () => void;
  waivers?: GetWaiversData[];
  depositStatus?: DepositPaymentStatus | null;
  onWaiverPress?: () => void;
};

function AppointmentSidebarStatusHeader({
  appointment,
  primaryAction,
  primaryActionLoading,
  kebabGroups,
  showEdit,
  onEdit,
  onClose,
  waivers,
  depositStatus,
  onWaiverPress,
}: Props) {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const kebabOpen = Boolean(anchorEl);

  const plainStatusLabel = PLAIN_LABEL_BY_STATUS[appointment.status];
  const statusKey = plainStatusLabel
    ? null
    : getAppointmentStatusKey(appointment);

  const depositIsPaid = isAppointmentDepositPaid(appointment);
  const hasDeposit = (appointment.depositPrice ?? 0) > 0;
  const isCompleted = getAppointmentStatusKey(appointment) === 'completed';
  const depositAmountText = formatDollarAmount(appointment.depositPrice ?? 0);
  // Once checked out the deposit is settled, so the badge adds nothing.
  const depositBadge =
    !hasDeposit || isCompleted
      ? null
      : appointment.status === 'CANCELED'
      ? depositStatus === 'REFUNDED'
        ? {
            text: `${depositAmountText} deposit refunded`,
            palette: 'canceled' as const,
          }
        : depositStatus === 'VOIDED'
        ? {
            text: `${depositAmountText} deposit voided`,
            palette: 'canceled' as const,
          }
        : depositStatus === 'FORFEITED'
        ? {
            text: `${depositAmountText} deposit forfeited`,
            palette: 'canceled' as const,
          }
        : depositIsPaid
        ? {
            text: `${depositAmountText} deposit paid`,
            palette: 'complete' as const,
          }
        : null
      : depositIsPaid
      ? {
          text: `${depositAmountText} deposit paid`,
          palette: 'complete' as const,
        }
      : { text: `${depositAmountText} deposit due`, palette: 'due' as const };

  const { waiverRequest } = appointment;
  const waiverStatus = waiverRequest
    ? getWaiverRequestStatus({ appointment, waiverRequest, waivers })
    : null;
  const waiverPalette =
    appointment.status === 'CANCELED' || waiverStatus === 'canceled'
      ? ('canceled' as const)
      : waiverStatus === 'complete'
      ? ('complete' as const)
      : waiverStatus === 'pendingEmployee'
      ? ('pendingEmployee' as const)
      : ('notSent' as const);

  const hasKebabActions = kebabGroups.some(group => group.actions.length > 0);

  return (
    <Box className="appointment-sidebar-status-header">
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 14px',
          background: 'var(--sidebar-header-bg)',
          borderBottom: '1px solid var(--sidebar-hairline)',
        }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <IconButton
            aria-label="Close"
            onClick={onClose}
            sx={{
              width: 28,
              height: 28,
              padding: 0,
              border: '1px solid var(--sidebar-ink)',
              borderRadius: '5px',
            }}>
            <CloseIcon sx={{ fontSize: 13 }} />
          </IconButton>
          <Typography
            sx={{
              fontSize: 'var(--sidebar-font-md)',
              fontWeight: 700,
              color: 'var(--sidebar-ink)',
            }}>
            {appointment.appointmentType === 'CONSULTATION'
              ? 'Consultation'
              : 'Appointment'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          {showEdit && onEdit && (
            <Box
              component="button"
              type="button"
              aria-label="Edit"
              onClick={onEdit}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                height: 28,
                padding: '0 12px',
                borderRadius: 50,
                border: '1px solid var(--sidebar-ink)',
                background: 'transparent',
                fontSize: 'var(--sidebar-font-sm)',
                fontWeight: 700,
                color: 'var(--sidebar-ink)',
                cursor: 'pointer',
              }}>
              <EditIcon sx={{ fontSize: 12 }} />
              Edit
            </Box>
          )}

          {hasKebabActions && (
            <>
              <IconButton
                aria-label="More actions"
                aria-controls={kebabOpen ? 'appointment-kebab-menu' : undefined}
                aria-haspopup="true"
                onClick={event => setAnchorEl(event.currentTarget)}
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  ...(kebabOpen && {
                    background: 'var(--sidebar-ink)',
                    color: 'var(--sidebar-surface)',
                  }),
                }}>
                <MoreHorizIcon fontSize="small" />
              </IconButton>
              <Menu
                id="appointment-kebab-menu"
                anchorEl={anchorEl}
                open={kebabOpen}
                onClose={() => setAnchorEl(null)}
                MenuListProps={{ sx: { padding: '6px 0' } }}
                slotProps={{
                  paper: {
                    sx: {
                      width: 268,
                      maxWidth: 'calc(100vw - 24px)',
                      border: '1px solid var(--sidebar-ink)',
                      borderRadius: '10px',
                      boxShadow: '0 12px 32px rgba(0,0,0,0.25)',
                    },
                  },
                }}>
                {kebabGroups
                  .filter(group => group.actions.length > 0)
                  .flatMap((group, groupIndex) => [
                    groupIndex > 0 && (
                      <Divider
                        key={`divider-${group.group}`}
                        sx={{
                          borderColor: 'var(--sidebar-hairline)',
                          margin:
                            group.group === 'Danger' ? '6px 0' : '6px 0 0',
                        }}
                      />
                    ),
                    // The red items say it themselves.
                    group.group !== 'Danger' && (
                      <ListSubheader
                        key={`header-${group.group}`}
                        sx={{
                          position: 'static',
                          lineHeight: 'normal',
                          padding: '10px 14px 6px',
                          fontSize: 'var(--sidebar-font-2xs)',
                          fontWeight: 700,
                          letterSpacing: '0.06em',
                          textTransform: 'uppercase',
                          color: 'var(--sidebar-tertiary)',
                        }}>
                        {group.group}
                      </ListSubheader>
                    ),
                    ...group.actions.map(action => (
                      <MenuItem
                        key={action.label}
                        onClick={() => {
                          setAnchorEl(null);
                          action.action();
                        }}
                        sx={{
                          padding: '9px 14px',
                          fontSize: 'var(--sidebar-font-base)',
                          fontWeight: 600,
                          color: 'var(--sidebar-body)',
                          '&:hover': { background: 'var(--sidebar-header-bg)' },
                          ...(group.group === 'Danger' && {
                            fontWeight: 700,
                            color: 'var(--sidebar-danger)',
                            '&:hover': {
                              background: 'var(--sidebar-danger-bg)',
                            },
                          }),
                        }}>
                        {action.label}
                      </MenuItem>
                    )),
                  ])
                  .filter(Boolean)}
              </Menu>
            </>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          padding: '14px',
          background: 'var(--sidebar-surface)',
          boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
        }}>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 0.75,
          }}>
          {statusKey ? (
            <StatusChip status={statusKey} size="detail" />
          ) : (
            plainStatusLabel && (
              <Badge text={plainStatusLabel} colorScheme="grey" rounded />
            )
          )}
          {depositBadge && (
            <SecondaryBadge
              text={depositBadge.text}
              palette={depositBadge.palette}
            />
          )}
          {!!waiverRequest && (
            <SecondaryBadge
              text="Waiver"
              palette={waiverPalette}
              onClick={onWaiverPress}
            />
          )}
        </Box>

        {primaryAction && (
          <LoadingButton
            variant="contained"
            className="primary"
            loading={primaryActionLoading}
            onClick={primaryAction.action}
            sx={{
              width: '100%',
              height: '44px',
              borderRadius: '50px',
              fontSize: 'var(--sidebar-font-button)',
              fontWeight: 700,
            }}>
            {primaryAction.label}
          </LoadingButton>
        )}
      </Box>
    </Box>
  );
}

export default AppointmentSidebarStatusHeader;
